import { LocationsAttributes } from '../../../../backbone-unorganized/models';
import {
    BaseZenputCollection,
    BaseZenputModel,
    FormSmetadata,
    FormStructure,
    Paginator,
    SearchCriteria,
    SearchCriterias,
    Submission,
    Submissions
} from '../../../../backbone-unorganized/shared/forms';
import { MobileAccountField } from '../accountField/models';
import { AttributeCriteriaFilterSet, MobileBaseField } from '../BaseField/models';
import { MobileDateTimeField } from '../datetimeField/models';
import { MobileDocumentField } from '../documentField/models';
import { MobileEmailField } from '../emailField/models';
import {
    MobileEquipmentTemperatureField,
    MobileIngredientTemperatureField
} from '../ingredientEquipmentTemperatureFields/models';
import { MediaFieldFormSubmission, LocationFieldOrder } from '../models';
import { MobileNumberField } from '../numberField/models';
import { MobileSectionField } from '../sectionField/models';
import { MobileSensorTemperatureField } from '../sensorTemperatureField/models';
import { MobileOilQualityField } from '../oilQualityField/models';
import { MobileSignatureField } from '../signatureField/models';
import { MobileStopwatchField } from '../stopwatchField/models';
import { MobileTemperatureField } from '../temperatureField/models';
import { MobileYesNoField } from '../yesNoField/models';
import { isConnected } from '../../../../shared/utils/is';
import debounce from 'lodash.debounce';
import { isReleaseBuild } from '../../../../shared/utils/debugging';

const FORM_SAVE_DEBOUNCE_MS = 300;

export const FormStructureFieldMapItem = BaseZenputModel.extend({
    defaults: {
        id: null,
        next_id: null,
        prev_id: null
    }
});

/**
 * A mapping of field mappings in a FormStructure, properly ordered.
 *
 */
export const FormStructureFieldMap = BaseZenputCollection.extend({
    model: FormStructureFieldMapItem,
    form_structure: null,

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);
    },

    /**
     * Backbone's .add() is widly imperformant because it turns model into an array and merges it.
     * Because we are building this collection model by model as we loop through the form collection,
     * it's important that we don't do that.
     */
    add_single: function (model) {
        if (!(model instanceof Backbone.Model)) {
            model = new this.model(model);
        }

        this.models.push(model);
        this._byId[model.id] = model; // used for this.get(<id>)
        this.length++;
    },

    get_next: function (head_id) {
        var model = this.get(head_id);
        if (!model) {
            return null;
        }

        return this.get(model.get('next_id'));
    },

    get_prev: function (head_id) {
        var model = this.get(head_id);
        if (!model) {
            return null;
        }

        return this.get(model.get('prev_id'));
    },

    get_next_invalid_id: function (head_id) {
        var next = this.get_next(head_id);
        if (!next) {
            return null;
        }

        if (!this.form_structure.get(next.id).is_valid) {
            // @author:david don't use isValid(true), it's expensive
            return next.get('id');
        }
        return this.get_next_invalid_id(next.id);
    },

    get_prev_invalid_id: function (head_id) {
        var prev = this.get_prev(head_id);

        if (!prev) {
            return null;
        }

        if (!this.form_structure.get(prev.id).is_valid) {
            // @author:david don't use isValid(true), it's expensive
            return prev.get('id');
        }

        return this.get_prev_invalid_id(prev.id);
    }
});

/**
 * Our mobile version of FormStructure.  Note the model points to
 * our mobile BaseField
 */
export const MobileFormStructure = FormStructure.extend({
    save_url: '/api/v1/forms/save/',
    field_map: null,

    _submission_history_by_account: null, // {} - used for YesNo field history

    model: function (attrs, options) {
        switch (attrs.type) {
            case 'section':
                return new MobileSectionField(attrs, options);
            case 'number':
                return new MobileNumberField(attrs, options);
            case 'stopwatch':
                return new MobileStopwatchField(attrs, options);
            case 'email':
                return new MobileEmailField(attrs, options);
            case 'image':
            case 'video':
                return new MediaFieldFormSubmission(attrs, options);
            case 'signature':
                return new MobileSignatureField(attrs, options);
            case 'document':
                return new MobileDocumentField(attrs, options);
            case 'datetime':
                return new MobileDateTimeField(attrs, options);
            case 'account':
                return new MobileAccountField(attrs, options);
            case 'yesno':
                return new MobileYesNoField(attrs, options);
            case 'temperature':
                return new MobileTemperatureField(attrs, options);
            case 'ingredient_temperature':
                return new MobileIngredientTemperatureField(attrs, options);
            case 'equipment_temperature':
                return new MobileEquipmentTemperatureField(attrs, options);
            case 'sensor_temperature':
                return new MobileSensorTemperatureField(attrs, options);
            case 'oil_quality':
                return new MobileOilQualityField(attrs, options);
            default:
                return new MobileBaseField(attrs, options);
        }
    },

    initialize: function () {
        /**
         * For some fucked up reason, Backbone wont fir the 'change' event as of 1.1.0 and
         * Marionette 1.5.  We must instead listen to 'all' and filter out only change
         *
         * Search for @note:change_all
         */

        this._submission_history_by_account = this._submission_history_by_account || {};

        this.field_map = new FormStructureFieldMap([], {
            form_structure: this
        });

        this.locationFieldOrder = new LocationFieldOrder();

        const debouncedSaveActiveCollection = debounce(() => {
            $f.mobile.save_active_collection(this);
        }, FORM_SAVE_DEBOUNCE_MS);

        this.on('all', function (event, model) {
            if (event != 'change') {
                return;
            }

            // upon rendering we must calculate default formulas for all fields
            if (this.is_new()) {
                $f.utils.formula.calculate_all(this);
            }

            // be sure we set GUID last, so we can check for 'new' and run other functions
            this.set_guid();
            this.smetadata.set('date_modified', { $date: new Date().getTime() });
            debouncedSaveActiveCollection();
        });

        this.on('account:change dynamicform:first_load', (location_id) => {
            this._handle_location_change(location_id);
            this._handle_location_order(location_id);
        });

        this._activityFieldIdsToOrderId = _.memoize((activityFieldId) => {
            const targetField = this.find(
                (field) => Number(field.get('activity_field_data').field_id) === activityFieldId
            );
            return targetField.id;
        });
    },

    _handle_location_order: function (location_id) {
        const hasOrderByLocation = this.metadata.get('order_fields_by_location');
        if (!hasOrderByLocation) {
            return;
        }
        if (!location_id && !this._lastLocationId) {
            return;
        }

        this._lastLocationId = location_id;
        if (!_.isNumber(location_id)) {
            this.locationFieldOrder.clear();
            this.trigger('reset');
            // TODO: This is ugly, but we need to re-trigger dynamic visibility after fields are sorted
            this.trigger('dynamicform:location_attributes_fetched', null);
            return;
        }

        const activity_template = this.metadata.get('activity_template');

        if (!activity_template) {
            return;
        }

        const previousOrder = this.get_sorted_fields().map((model) => model.get('id'));

        this.locationFieldOrder.set({
            activity_template,
            location: {
                id: location_id
            }
        });
        this.locationFieldOrder.once('sync', () => {
            const currentOrder = this.get_sorted_fields().map((model) => model.get('id'));
            if (!_.isEqual(previousOrder, currentOrder)) {
                this.trigger('reset');
            }
        });
        this.locationFieldOrder.fetch();
    },

    /**
     * Fetch location attributes if needed when a location changes, triggers dynamicform:location_attributes_fetched
     * along with the location attributes if available
     */
    _handle_location_change: async function (location_id) {
        // if location_id is not a number, still triggers the event but with null location attributes
        // this condition is met when removing a selected location or at first load when location hasn't been selected yet
        if (!_.isNumber(location_id)) {
            this._locationAttributes = null;
            this.trigger('dynamicform:location_attributes_fetched', null);
            return;
        }

        try {
            const location_attributes = await LocationsAttributes.get_location_attributes(location_id);
            this._locationAttributes = location_attributes;
            this.trigger('dynamicform:location_attributes_fetched', location_attributes);
        } catch (err) {
            if (!isConnected()) {
                return this.trigger('dynamicform:location_attributes_unavailable');
            }

            throw new Error(`Location Attributes fetching error: ${err}`);
        }
    },

    serialize_for_reordering: function () {
        return this.get_sorted_fields().map((model) => {
            const hasDynamicVisibility =
                model.is_dynamic_visibility_allowed() &&
                model.get_metadata_attribute('dynamic_visibility') &&
                this._locationAttributes;
            const serializedModel = model.toJSON();
            if (!hasDynamicVisibility) {
                return {
                    ...serializedModel,
                    is_visible: true
                };
            }
            const dynamic_visibility = model.get_metadata_attribute('dynamic_visibility');
            const attribute_criteria_filter_set = dynamic_visibility.attribute_criteria_filters
                ? new AttributeCriteriaFilterSet(dynamic_visibility.attribute_criteria_filters)
                : null;
            const meetsCriteria = attribute_criteria_filter_set
                ? attribute_criteria_filter_set.evaluate(this._locationAttributes)
                : true; // If attribute_criteria_filters is not present, consider it as meeting the criteria
            const isDynamicHide = dynamic_visibility.visibility_choice === 'hide';
            // Note: `^` is XOR operator - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_XOR
            const isVisible = Boolean(meetsCriteria ^ isDynamicHide);
            return {
                ...serializedModel,
                is_visible: isVisible
            };
        });
    },

    set_location_order: function () {
        const hasOrderByLocation = this.metadata.get('order_fields_by_location');
        if (!hasOrderByLocation) {
            return;
        }
        const order = this.locationFieldOrder.get('field_order');

        if (order && !_.isEmpty(order.field_order)) {
            this.smetadata.set('location_custom_field_order', order.field_order, {
                silent: true
            });
        }
    },

    /**
     * This is used to uniquely identify a Form Submission.  First we make sure date_created is
     * set, then we create a unique GUID.
     *
     * This is only in Mobile models so no webapp user could ever call it, for now
     */
    set_guid: function () {
        if (!this.smetadata.get('date_created') || !this.smetadata.get('date_created').$date) {
            this.smetadata.set('date_created', { $date: new Date().getTime() });
        }

        if (!this.smetadata.get('guid')) {
            var guid = this.metadata.get('rdbms_id') + '-' + this.smetadata.get('date_created').$date;
            this.smetadata.set('guid', guid);
        }
    },

    is_new: function () {
        return this.smetadata.get('guid') == null;
    },

    /**
     * Check if this form has previously been submitted.
     * @return {Boolean} True if this collection has already been submitted, false if it has not.
     */
    has_been_submitted: function () {
        if (this.smetadata.get('date_submitted') && this.smetadata.get('date_submitted').$date) {
            return true;
        }
        if (this._id && this._id.$oid) {
            return true;
        }
        return false;
    },

    /**
     * Determine if the form has started to be filled out
     * @return {Boolean} Whether or not the form has been started
     */
    is_started: function () {
        return this.smetadata.get('date_modified') != null;
    },

    is_draft: function () {
        return this.smetadata.get('draft') === true;
    },

    /**
     * Clears the forms smetadata, _id, and media fields (images, videos, and signatures). This is used for
     * loading a previous submission into a draft submission.
     */
    clear_smetadata_and_media_fields: function () {
        this.smetadata = new FormSmetadata();
        this.smetadata.set('date_modified', { $date: new Date().getTime() });
        delete this._id;

        var media_models = [];
        media_models = media_models.concat(this.where({ type: 'image' }));
        media_models = media_models.concat(this.where({ type: 'video' }));
        media_models = media_models.concat(this.where({ type: 'signature' }));
        media_models = media_models.concat(this.where({ type: 'document' }));

        $.each(media_models, function (i, model) {
            model.set({
                file: null,
                value: null,
                notes: ''
            });
        });
    },

    /**
     * Build activity submission data format from the existing form template collection.
     *
     * @returns {
     *      answers: {field_id: {field_version_id, value, acceptance_value?}, ...},
     *      smetadata: *,
     *      activity_template: *
     * }
     */
    construct_activity_submission_data: function () {
        var answers = this.reduce(function (accumulator, field) {
            var activity_field_data = field.get('activity_field_data');
            if (!activity_field_data) {
                throw new Error('No activity_field_data found in legacy field.');
            }
            var field_id = activity_field_data.field_id;
            var field_version_id = activity_field_data.field_version_id;

            // each model process it's own value before submission
            accumulator[field_id] = {
                field_version: { id: field_version_id },
                value: field.get_submission_value(),
                visible: field.get('visible')
            };

            // conditionally add acceptance value only if it has been set.
            var acceptance_value = field.get('acceptance_value');
            if (acceptance_value !== null) {
                accumulator[field_id].acceptance_value = acceptance_value;
            }
            // conditionally add notes only if it has been set.
            var notes = field.get('notes');
            if (notes !== null) {
                accumulator[field_id].notes = notes;
            }

            if (
                (field.get('type') === 'oil_quality' || field.get('type') === 'temperature') &&
                field.get('reading_method')
            ) {
                accumulator[field_id].reading_method = field.get('reading_method');
            }

            return accumulator;
        }, {});

        return {
            smetadata: this.smetadata.toJSON(),
            activity_template: this.metadata.get('activity_template'),
            answers: answers
        };
    },

    save_to_new_submission_endpoint: function (options) {
        options = $f.utils.setup_options_callbacks(options);
        var save_url = '/api/v2/submissions/';
        var post_data = JSON.stringify(this.construct_activity_submission_data());
        if (!isReleaseBuild()) {
            // eslint-disable-next-line no-console
            console.log('Post data: ', post_data);
        }
        var gzip_binary = window.pako.deflate(post_data);
        // todo: review all of these ajax options copied from below.
        var retry = false;
        var ajax_data = {
            url: save_url,
            dataType: 'json',
            type: 'POST',
            cache: false,
            timeout: 60000, // upped this to a 60 second timeout for slow client connections
            data: gzip_binary,
            // note that we are properly setting content-type here. we do not do this anywhere else
            contentType: 'application/bson',
            headers: {
                'Content-Encoding': 'gzip'
            },
            validate: true,
            processData: false,
            emulateHTTP: false,
            emulateJSON: false,
            success: function (response) {
                options.success(response);
            },
            error: function (response) {
                if (response.status === 0) {
                    if (!retry) {
                        retry = true;
                        $f.mobile.ajax(ajax_data);
                    }
                    $f.mobile.logging.log_error('Error submitting activity submission', {
                        length: post_data.length,
                        data: post_data,
                        response
                    });
                }
                options.error(response);
            }
        };
        $f.mobile.ajax(ajax_data);
    },

    save: function (options) {
        // only send to the new submission endpoint if this is not saving a draft
        //  and we see the correct new data in metadata
        if (this.metadata.get('activity_template') !== null && this instanceof FormDraft === false) {
            try {
                return this.save_to_new_submission_endpoint(options);
            } catch (e) {
                // continue with normal legacy save endpoint below in case of error
                $f.mobile.logging.log_error('Failed to send submission with activity template data', {
                    e: e
                });
            }
        }
        options = $f.utils.setup_options_callbacks(options);
        var post_data = JSON.stringify(this.toJSON());
        if (!isReleaseBuild()) {
            // eslint-disable-next-line no-console
            console.log('Post data: ', post_data);
        }
        var gzip_binary = window.pako.deflate(post_data);
        $f.mobile.ajax({
            url: this.save_url,
            dataType: 'json',
            type: 'POST',
            cache: false,
            timeout: 60000, // upped this to a 60 second timeout for slow client connections
            data: gzip_binary,
            contentType: 'application/json',
            headers: {
                'Content-Encoding': 'gzip'
            },
            validate: true,
            processData: false,
            emulateHTTP: false,
            emulateJSON: false,
            success: function (response) {
                options.success(response);
            },
            error: function (response) {
                // Adding back this logging for form submission saves. avoid already_updated since that is an
                // expected 500 error from draft/save.
                if (!response.already_updated) {
                    $f.mobile.logging.log_error('Error submitting form - logging data sent', {
                        length: post_data.length,
                        data: post_data
                    });
                }
                options.error(response);
            }
        });
    },

    delete: function () {
        throw new Error('Programmer Error: MobileFormStructure does not have a delete endpoint');
    },

    get_sorted_fields: function () {
        const order = this.get_order();
        return this.models.sort((modelA, modelB) => order.indexOf(modelA.get('id')) - order.indexOf(modelB.get('id')));
    },

    /**
     * For whatever reason, our order array is all strings, not ints, which isn't consistent with model IDs.
     * This will give us an order array with actual ints.
     */
    get_order: function () {
        const metadataOrder = this.metadata.get('order');
        const smetadataOrder = this.smetadata.get('location_custom_field_order');
        const locationOrder = this.locationFieldOrder.get('field_order');
        const hasOrderByLocation = this.metadata.get('order_fields_by_location');

        if (smetadataOrder) {
            return smetadataOrder.map(this._activityFieldIdsToOrderId);
        }

        if (hasOrderByLocation && locationOrder && locationOrder.field_order) {
            return locationOrder.field_order.map(this._activityFieldIdsToOrderId);
        }

        return metadataOrder.map((value) => {
            const parsed = Number(value);
            if (isNaN(parsed)) {
                return value;
            }
            return parsed;
        });
    },

    add_item_to_field_map: function (model) {
        var order = this.get_order();
        var index = order.indexOf(model.id);

        this.field_map.add_single({
            id: model.id,
            next_id: order[index + 1] !== 'undefined' ? order[index + 1] : null,
            prev_id: typeof order[index - 1] !== 'undefined' ? order[index - 1] : null
        });
    },

    /**
     * @warning @author:david on large forms, it's very expensive to call isValid(true) over and over
     * on large forms (each isValid(true) takes ~3 ms. 3 * 800 = 2.4 sec UI lock!!).
     * Thus, we will rely on our own pre-calculated model.is_valid flag as much as we can,
     * which should be pre-calculated on form load and whenever a field value changes.
     */

    /**
     * Get the total number of invalid fields in this collection.
     * @param  {boolean}   trigger_events  If true, 'validated:(in)valid' events will be triggered for view to handle.
     * @return {int}                       The number of invalid fields
     */
    get_total_invalid: function (trigger_events) {
        var length = 0;
        this.each(function (model) {
            if (model.unsupported_field_type) {
                return true;
            } // continue

            // perform below check since we always consider fields that are hidden because of
            // dynamic visibility to be valid
            if (!model.get('visible')) {
                if (trigger_events) {
                    model.trigger('validated:valid', model);
                }
                return;
            }

            if (model.get('type') === 'ingredient_temperature' || model.get('type') === 'equipment_temperature') {
                length += model.get_num_invalid();

                if (trigger_events) {
                    model.trigger('validated:invalid', model);
                }
            } else if (!model.is_valid) {
                length++;

                if (trigger_events) {
                    model.trigger('validated:invalid', model);
                }
            } else if (trigger_events) {
                model.trigger('validated:valid', model);
            }
        });

        return length;
    },

    get_first_invalid: function () {
        var invalid_model = null;

        this.each(function (model) {
            if (invalid_model) {
                return true;
            } // continue, already found it
            if (model.is_valid) {
                return true;
            } // continue, not what we're looking for

            invalid_model = model;
        });

        return invalid_model;
    },

    /**
     * Get a list of submissions, either for the entire form template or for a specified account_id
     * @param  {object}     options                  Options for the search
     * @param  {int}        options.account_field    An account field with an account in it to search by
     * @param  {function}   options.success          A success callback with an
     *                                               MobileSubmissions instance passed in
     * @param  {function}   options.error            An error callback
     */
    get_previous_submissions: function (options) {
        options = options || {};
        options.success = options.success || function () {};
        options.error = options.error || function () {};

        var this_ = this;

        if (!options.account_field) {
            $f.mobile.logging.log_error('Programmer Error: account_field is required if account_id is specified');
            return options.error(null);
        }

        try {
            var account_id = options.account_field.get('account').id;
        } catch (e) {
            return options.error();
        }

        if (this._submission_history_by_account[account_id]) {
            return options.success(this._submission_history_by_account[account_id]);
        }

        if (!isConnected()) {
            return options.error(null);
        }

        var search_criteria_list = new SearchCriterias([], {
            metadata: {
                next_criteria_id: 1,
                simple_logic: 'and',
                display_result_type: 'list',
                rdbms_id: this.metadata.get('rdbms_id')
            },

            result_type: {
                list: {
                    sort: [
                        {
                            field_id: 'smetadata.date_submitted',
                            order: -1
                        }
                    ],
                    columns: [],
                    type: 'list'
                }
            }
        });

        search_criteria_list.add(
            new SearchCriteria({
                field_id: options.account_field.id,
                operator_id: '=',
                value: `account~${account_id}`
            })
        );

        // For submission view, we should only show yes/no's PREVIOUS to this submission date.
        if (this.has_been_submitted()) {
            search_criteria_list.add(
                new SearchCriteria({
                    field_id: 'smetadata.date_submitted',
                    operator_id: 'date_specific_before',
                    value: moment(this.smetadata.get('date_submitted').$date).format('YYYY-MM-DD HH:mm:ss')
                })
            );
        }

        // @author:david using a hard limit of 5 here and not accepting limit from options, since these
        // submissions are cached in memory (@see this._submission_history_by_account) and may be used by multiple
        // features (e.g., yes/no history and submission prefill). So, using 5 as the upper bound.
        var submissions = new MobileSubmissions([], {
            search_criterias: search_criteria_list,
            paginator: new Paginator({ limit: 5, data_source: 'mongo' }),
            rdbms_id: this.metadata.get('rdbms_id')
        });

        submissions.fetch({
            success: function (subs) {
                // @author:david For some reason, using submissions (above) does not work when
                // page is loading. Some sort of scope issue? Using 'subs' to get around it.

                this_._submission_history_by_account[account_id] = subs; // cache our results later

                options.success(subs);
            },
            error: options.error
        });
    },

    /**
     * Autofill from previous submission
     * For each field, copy in the relevant data from the past submission
     * All updates are called with silent:true, so caller must handle any necessary 'change' events
     *
     * @param {MobileFormStructure} - past submission
     */
    autofill_from_previous_submission: function (past_submission) {
        var this_ = this;
        past_submission.each(function (old_field) {
            var current_field = this_.findWhere({ id: old_field.get('id') });
            if (!current_field) {
                return true;
            }
            current_field.copy_from_past_submission_field(old_field);
        });
    },

    convertToOrderableFields: function () {
        const fields_order = this.metadata.get('order');
        return fields_order.map((id) => {
            const field = this.get(id);
            return { id: field.id, text: field.get('title'), type: field.get('type') };
        });
    }
});

/**
 * Draft version our of form data. Different endpoints for saving
 */
export const FormDraft = MobileFormStructure.extend({
    save_url: '/api/v1/forms/drafts/save/',
    delete_url: '/api/v1/forms/drafts/delete/',

    delete: function (options) {
        options = $f.utils.setup_options_callbacks(options);
        var url = `${this.delete_url}${this.smetadata.get('guid')}/`;
        $f.mobile.ajax({
            url: url,
            dataType: 'json',
            type: 'GET',
            contentType: 'application/json',
            success: function (response) {
                options.success();
            },
            error: function (response) {
                options.error();
            }
        });
    }
});

/**
 * A mobile version of Submission that uses our mobile MobileFormStructure
 */
export const MobileSubmission = Submission.extend({
    url: '/api/v1/forms/',

    initialize: function (options) {
        MobileSubmission.__super__.initialize.apply(this, arguments);
        var this_ = this;

        this._convert_json_to_models();
        this.on('change', function () {
            this_._convert_json_to_models();
        });
    },

    sync: function (method, model, options) {
        if (method != 'read') {
            throw new Error('Programmer Error: create/update not yet implemented for Submission');
        }

        options.url = `${model.url}get/${model.id || ''}?include_rules=true`;
        return Backbone.sync(method, model, options);
    },

    parse: function (response) {
        if (response.fields) {
            return {
                form_structure: response,
                expanded_metadata: response.expanded_metadata
            };
        }

        return response;
    },

    /**
     * Make sure this.get('form_structure') is always 1) a Backbone Collection, and 2) an $f.MOBILE.models.FormStructure
     */
    _convert_json_to_models: function () {
        if (!this.get('form_structure')) {
            this.set('form_structure', new MobileFormStructure(), { silent: true });
            return;
        }

        if (!(this.get('form_structure') instanceof Backbone.Collection)) {
            this.set('form_structure', new MobileFormStructure(this.get('form_structure')), { silent: true });
        } else if (!(this.get('form_structure') instanceof MobileFormStructure)) {
            this.set('form_structure', new MobileFormStructure(this.get('form_structure').toJSON()));
        }
    }
});

/**
 * Our mobile version of Submissions that uses MobileFormStructure
 * instead of FormStructure.
 */
export const MobileSubmissions = Submissions.extend({
    model: MobileSubmission
});

/**
 * A simple collection of submissions, for a specified account.
 */
export const AccountSubmissions = MobileSubmissions.extend({
    cache_enabled: true,
    cache_ttl: 120000, // 2 minutes
    account: null, // Account - the account we want to find submissions for

    initialize: function (models, options) {
        this.search_criterias = new SearchCriterias([], {
            metadata: {
                next_criteria_id: 1,
                simple_logic: 'and',
                display_result_type: 'list'
            },
            result_type: {
                list: {
                    sort: [{ field_id: 'smetadata.date_submitted', order: -1 }],
                    columns: [],
                    type: 'list'
                }
            }
        });

        AccountSubmissions.__super__.initialize.apply(this, arguments);

        if (!this.account) {
            throw new Error('Programmer error: please include a Account model with AccountSubmissions');
        }
        this.cache_key = 'submissions_for_account_id_' + this.account.id;
    },

    url: function () {
        return '/api/v1/accounts/account/list_submissions/' + this.account.get('id');
    }
});
